import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Calendar,
  Clock,
  DollarSign,
  Plus,
  Edit,
  Trash2,
  Save,
  Settings,
  Users,
  MapPin,
  Phone,
  Mail,
  FileText,
  ToggleLeft,
  ToggleRight,
  X,
  Upload,
  Image as ImageIcon,
} from "lucide-react";

// Import the image uploader component
import ImageUploader from "./page-management/ImageUploader";

const BookingManagement = () => {
  const [services, setServices] = useState([]);
  const [activeTab, setActiveTab] = useState("services");
  const [showServiceForm, setShowServiceForm] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [isMobile, setIsMobile] = useState(false);

  const [serviceForm, setServiceForm] = useState({
    name: "",
    slug: "",
    description: "",
    category: "driving_class",
    heroImage: "", // Add hero image field
    formFields: [],
    availableDays: [],
    timeSlots: [
      {
        startTime: "09:00",
        endTime: "17:00",
        duration: 60,
        capacity: 1,
        buffer: 15,
      },
    ],
    pricing: {
      basePrice: 0,
      pricingType: "fixed",
      pricePerMinute: 0,
      optionPricing: [],
    },
    advanceBookingDays: 30,
    minNoticeHours: 2,
    maxBookingsPerCustomer: 0,
    requiresPayment: false,
    paymentType: "full_upfront",
    depositAmount: 0,
    hasGST: false,
    hasPST: false,
    isActive: true,
  });

  const [newField, setNewField] = useState({
    fieldId: "",
    label: "",
    type: "text",
    required: false,
    placeholder: "",
    options: [""],
    order: 0,
  });

  const fieldTypes = [
    { value: "text", label: "Text Input" },
    { value: "textarea", label: "Text Area" },
    { value: "select", label: "Dropdown" },
    { value: "radio", label: "Radio Buttons" },
    { value: "checkbox", label: "Checkboxes" },
    { value: "date", label: "Date" },
    { value: "time", label: "Time" },
    { value: "number", label: "Number" },
    { value: "email", label: "Email" },
    { value: "phone", label: "Phone" },
  ];

  const categories = [
    { value: "driving_class", label: "Driving Class" },
    { value: "restaurant", label: "Restaurant" },
    { value: "appointment", label: "Appointment" },
    { value: "consultation", label: "Consultation" },
    { value: "other", label: "Other" },
  ];

  const daysOfWeek = [
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
    "sunday",
  ];

  useEffect(() => {
    const checkIsMobile = () => setIsMobile(window.innerWidth < 768);
    checkIsMobile();
    window.addEventListener("resize", checkIsMobile);
    return () => window.removeEventListener("resize", checkIsMobile);
  }, []);

  useEffect(() => {
    fetchServices();
    if (activeTab === "bookings") {
      fetchBookings();
    }
  }, [activeTab]);

  const fetchServices = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get("/api/admin/booking-services", {
        headers: { Authorization: `Bearer ${token}` },
      });
      setServices(response.data);
    } catch (error) {
      console.error("Error fetching services:", error);
    }
  };

  const fetchBookings = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get("/api/admin/bookings", {
        headers: { Authorization: `Bearer ${token}` },
      });
      setBookings(response.data.bookings);
    } catch (error) {
      console.error("Error fetching bookings:", error);
    }
  };

  const handleCreateService = async () => {
    try {
      const token = localStorage.getItem("token");

      if (editingService) {
        await axios.put(
          `/api/admin/booking-services/${editingService._id}`,
          serviceForm,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
      } else {
        await axios.post("/api/admin/booking-services", serviceForm, {
          headers: { Authorization: `Bearer ${token}` },
        });
      }

      fetchServices();
      resetForm();
    } catch (error) {
      console.error("Error saving service:", error);
      alert(
        "Error saving service: " +
          (error.response?.data?.error || error.message)
      );
    }
  };

  const resetForm = () => {
    setServiceForm({
      name: "",
      slug: "",
      description: "",
      category: "driving_class",
      heroImage: "",
      formFields: [],
      availableDays: [],
      timeSlots: [
        {
          startTime: "09:00",
          endTime: "17:00",
          duration: 60,
          capacity: 1,
          buffer: 15,
        },
      ],
      pricing: {
        basePrice: 0,
        pricingType: "fixed",
        pricePerMinute: 0,
        optionPricing: [],
      },
      advanceBookingDays: 30,
      minNoticeHours: 2,
      maxBookingsPerCustomer: 0,
      requiresPayment: false,
      paymentType: "full_upfront",
      depositAmount: 0,
      hasGST: false,
      hasPST: false,
      isActive: true,
    });
    setEditingService(null);
    setShowServiceForm(false);
  };

  const handleEditService = (service) => {
    setEditingService(service);
    setServiceForm(service);
    setShowServiceForm(true);
  };

  const handleDeleteService = async (serviceId) => {
    if (!window.confirm("Are you sure you want to delete this service?"))
      return;

    try {
      const token = localStorage.getItem("token");
      await axios.delete(`/api/admin/booking-services/${serviceId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchServices();
    } catch (error) {
      console.error("Error deleting service:", error);
      alert("Error deleting service");
    }
  };

  // Handle hero image upload
  const handleHeroImageUpload = (imageUrl) => {
    setServiceForm({ ...serviceForm, heroImage: imageUrl });
  };

  const addFormField = () => {
    if (!newField.fieldId || !newField.label) {
      alert("Please fill in field ID and label");
      return;
    }

    const updatedFields = [...serviceForm.formFields, { ...newField }];
    setServiceForm({ ...serviceForm, formFields: updatedFields });
    setNewField({
      fieldId: "",
      label: "",
      type: "text",
      required: false,
      placeholder: "",
      options: [""],
      order: 0,
    });
  };

  const removeFormField = (index) => {
    const updatedFields = serviceForm.formFields.filter((_, i) => i !== index);
    setServiceForm({ ...serviceForm, formFields: updatedFields });
  };

  const addTimeSlot = () => {
    const newSlot = {
      startTime: "09:00",
      endTime: "17:00",
      duration: 60,
      capacity: 1,
      buffer: 15,
    };
    setServiceForm({
      ...serviceForm,
      timeSlots: [...serviceForm.timeSlots, newSlot],
    });
  };

  const updateTimeSlot = (index, field, value) => {
    const updatedSlots = [...serviceForm.timeSlots];
    updatedSlots[index][field] =
      field === "duration" || field === "capacity" || field === "buffer"
        ? parseInt(value)
        : value;
    setServiceForm({ ...serviceForm, timeSlots: updatedSlots });
  };

  const removeTimeSlot = (index) => {
    const updatedSlots = serviceForm.timeSlots.filter((_, i) => i !== index);
    setServiceForm({ ...serviceForm, timeSlots: updatedSlots });
  };

  const toggleDay = (day) => {
    const updatedDays = serviceForm.availableDays.includes(day)
      ? serviceForm.availableDays.filter((d) => d !== day)
      : [...serviceForm.availableDays, day];
    setServiceForm({ ...serviceForm, availableDays: updatedDays });
  };

  const updateBookingStatus = async (bookingId, newStatus) => {
    try {
      const token = localStorage.getItem("token");
      await axios.put(
        `/api/admin/bookings/${bookingId}/status`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchBookings();
    } catch (error) {
      console.error("Error updating booking status:", error);
    }
  };

  const renderServicesTab = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-xl font-semibold">Booking Services</h3>
        <button
          onClick={() => setShowServiceForm(true)}
          className="bg-blue-500 text-white px-4 py-2 rounded-lg flex items-center space-x-2 hover:bg-blue-600"
        >
          <Plus className="w-4 h-4" />
          <span>New Service</span>
        </button>
      </div>

      {services.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          No booking services created yet. Click "New Service" to get started.
        </div>
      ) : (
        <div className="grid gap-4">
          {services.map((service) => (
            <div key={service._id} className="bg-white rounded-lg shadow p-6">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    {service.heroImage && (
                      <img
                        src={service.heroImage}
                        alt={service.name}
                        className="w-12 h-12 rounded-lg object-cover"
                      />
                    )}
                    <div>
                      <h4 className="text-lg font-semibold">{service.name}</h4>
                      <p className="text-gray-600 text-sm">
                        {service.description}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4 mt-3 text-sm text-gray-500">
                    <span className="flex items-center">
                      <Settings className="w-4 h-4 mr-1" />
                      {service.category.replace("_", " ")}
                    </span>
                    <span className="flex items-center">
                      <DollarSign className="w-4 h-4 mr-1" />
                      {service.requiresPayment
                        ? `${service.pricing.basePrice}`
                        : "Free"}
                    </span>
                    <span className="flex items-center">
                      <Clock className="w-4 h-4 mr-1" />
                      {service.timeSlots[0]?.duration || 60} min
                    </span>
                  </div>
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => handleEditService(service)}
                    className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDeleteService(service._id)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderBookingsTab = () => (
    <div className="space-y-6">
      <h3 className="text-xl font-semibold">Recent Bookings</h3>

      {bookings.length === 0 ? (
        <div className="text-center py-8 text-gray-500">No bookings yet.</div>
      ) : (
        <div className="space-y-4">
          {bookings.map((booking) => (
            <div key={booking._id} className="bg-white rounded-lg shadow p-6">
              <div className="flex justify-between items-start">
                <div>
                  <h4 className="font-semibold">{booking.service?.name}</h4>
                  <p className="text-gray-600">#{booking.bookingNumber}</p>
                  <div className="flex items-center space-x-4 mt-2 text-sm text-gray-500">
                    <span className="flex items-center">
                      <Calendar className="w-4 h-4 mr-1" />
                      {new Date(booking.bookingDate).toLocaleDateString()}
                    </span>
                    <span className="flex items-center">
                      <Clock className="w-4 h-4 mr-1" />
                      {booking.startTime} - {booking.endTime}
                    </span>
                    <span className="flex items-center">
                      <Users className="w-4 h-4 mr-1" />
                      {booking.customerInfo.firstName}{" "}
                      {booking.customerInfo.lastName}
                    </span>
                  </div>
                </div>
                <div className="text-right">
                  <select
                    value={booking.status}
                    onChange={(e) =>
                      updateBookingStatus(booking._id, e.target.value)
                    }
                    className="border rounded px-3 py-1 text-sm"
                  >
                    <option value="pending">Pending</option>
                    <option value="confirmed">Confirmed</option>
                    <option value="completed">Completed</option>
                    <option value="cancelled">Cancelled</option>
                    <option value="no_show">No Show</option>
                  </select>
                  <div className="text-sm text-gray-500 mt-1">
                    ${booking.pricing.totalAmount.toFixed(2)}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderServiceForm = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-xl font-semibold">
              {editingService ? "Edit Service" : "Create New Service"}
            </h3>
            <button
              onClick={resetForm}
              className="text-gray-500 hover:text-gray-700"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          <div className="space-y-6">
            {/* Basic Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">
                  Service Name
                </label>
                <input
                  type="text"
                  value={serviceForm.name}
                  onChange={(e) =>
                    setServiceForm({ ...serviceForm, name: e.target.value })
                  }
                  className="w-full border rounded-lg px-3 py-2"
                  placeholder="e.g., Driving Lesson Class 5"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">
                  URL Slug
                </label>
                <input
                  type="text"
                  value={serviceForm.slug}
                  onChange={(e) =>
                    setServiceForm({ ...serviceForm, slug: e.target.value })
                  }
                  className="w-full border rounded-lg px-3 py-2"
                  placeholder="driving-lesson-class-5"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium mb-2">
                  Description
                </label>
                <textarea
                  value={serviceForm.description}
                  onChange={(e) =>
                    setServiceForm({
                      ...serviceForm,
                      description: e.target.value,
                    })
                  }
                  className="w-full border rounded-lg px-3 py-2"
                  rows="3"
                  placeholder="Describe your service..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">
                  Category
                </label>
                <select
                  value={serviceForm.category}
                  onChange={(e) =>
                    setServiceForm({ ...serviceForm, category: e.target.value })
                  }
                  className="w-full border rounded-lg px-3 py-2"
                >
                  {categories.map((cat) => (
                    <option key={cat.value} value={cat.value}>
                      {cat.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Hero Image Section */}
            <div className="border-t pt-6">
              <h4 className="text-lg font-medium mb-4 flex items-center">
                <ImageIcon className="w-5 h-5 mr-2" />
                Hero Image
              </h4>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Image URL
                  </label>
                  <input
                    type="url"
                    value={serviceForm.heroImage}
                    onChange={(e) =>
                      setServiceForm({
                        ...serviceForm,
                        heroImage: e.target.value,
                      })
                    }
                    className="w-full border rounded-lg px-3 py-2"
                    placeholder="https://example.com/image.jpg"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Enter an image URL or upload a file below
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">
                    Upload Image
                  </label>
                  <ImageUploader
                    onImageUpload={handleHeroImageUpload}
                    pageSlug={`booking-${serviceForm.slug || "service"}`}
                  />
                </div>
              </div>

              {/* Image Preview */}
              {serviceForm.heroImage && (
                <div className="mt-4">
                  <label className="block text-sm font-medium mb-2">
                    Preview
                  </label>
                  <div className="relative">
                    <img
                      src={serviceForm.heroImage}
                      alt="Hero image preview"
                      className="w-full h-48 object-cover rounded-lg border"
                      onError={(e) => {
                        e.target.style.display = "none";
                      }}
                    />
                    <button
                      type="button"
                      onClick={() =>
                        setServiceForm({ ...serviceForm, heroImage: "" })
                      }
                      className="absolute top-2 right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-red-600"
                    >
                      ×
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Available Days */}
            <div className="border-t pt-6">
              <label className="block text-sm font-medium mb-2">
                Available Days
              </label>
              <div className="flex flex-wrap gap-2">
                {daysOfWeek.map((day) => (
                  <button
                    key={day}
                    type="button"
                    onClick={() => toggleDay(day)}
                    className={`px-3 py-1 rounded-full text-sm ${
                      serviceForm.availableDays.includes(day)
                        ? "bg-blue-500 text-white"
                        : "bg-gray-200 text-gray-700"
                    }`}
                  >
                    {day.charAt(0).toUpperCase() + day.slice(1)}
                  </button>
                ))}
              </div>
            </div>

            {/* Time Slots */}
            <div className="border-t pt-6">
              <div className="flex justify-between items-center mb-2">
                <label className="block text-sm font-medium">Time Slots</label>
                <button
                  type="button"
                  onClick={addTimeSlot}
                  className="text-blue-500 hover:text-blue-700 text-sm"
                >
                  + Add Slot
                </button>
              </div>
              <div className="space-y-2">
                {serviceForm.timeSlots.map((slot, index) => (
                  <div key={index} className="p-3 border rounded">
                    <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
                      <div>
                        <input
                          type="time"
                          value={slot.startTime}
                          onChange={(e) =>
                            updateTimeSlot(index, "startTime", e.target.value)
                          }
                          className="border rounded px-2 py-1 w-full"
                        />
                      </div>
                      <div>
                        <input
                          type="time"
                          value={slot.endTime}
                          onChange={(e) =>
                            updateTimeSlot(index, "endTime", e.target.value)
                          }
                          className="border rounded px-2 py-1 w-full"
                        />
                      </div>
                      <div>
                        <input
                          type="number"
                          value={slot.duration}
                          onChange={(e) =>
                            updateTimeSlot(index, "duration", e.target.value)
                          }
                          placeholder="Duration (min)"
                          className="border rounded px-2 py-1 w-full"
                        />
                        <p className="text-xs text-gray-500 mt-1">Duration</p>
                      </div>
                      <div>
                        <input
                          type="number"
                          value={slot.buffer}
                          onChange={(e) =>
                            updateTimeSlot(index, "buffer", e.target.value)
                          }
                          placeholder="Buffer (min)"
                          className="border rounded px-2 py-1 w-full"
                        />
                        <p className="text-xs text-gray-500 mt-1">Buffer</p>
                      </div>
                      <div className="flex items-start justify-center lg:justify-start">
                        {serviceForm.timeSlots.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeTimeSlot(index)}
                            className="text-red-500 hover:text-red-700 mt-2"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Payment Settings */}
            <div className="border-t pt-6">
              <h4 className="text-lg font-medium mb-4">Payment Settings</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={serviceForm.requiresPayment}
                      onChange={(e) =>
                        setServiceForm({
                          ...serviceForm,
                          requiresPayment: e.target.checked,
                        })
                      }
                      className="rounded"
                    />
                    <span>Requires Payment</span>
                  </label>
                </div>
                {serviceForm.requiresPayment && (
                  <>
                    <div>
                      <label className="block text-sm font-medium mb-2">
                        Base Price ($)
                      </label>
                      <input
                        type="number"
                        value={serviceForm.pricing.basePrice}
                        onChange={(e) =>
                          setServiceForm({
                            ...serviceForm,
                            pricing: {
                              ...serviceForm.pricing,
                              basePrice: parseFloat(e.target.value) || 0,
                            },
                          })
                        }
                        className="w-full border rounded-lg px-3 py-2"
                        min="0"
                        step="0.01"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">
                        Pricing Type
                      </label>
                      <select
                        value={serviceForm.pricing.pricingType}
                        onChange={(e) =>
                          setServiceForm({
                            ...serviceForm,
                            pricing: {
                              ...serviceForm.pricing,
                              pricingType: e.target.value,
                            },
                          })
                        }
                        className="w-full border rounded-lg px-3 py-2"
                      >
                        <option value="fixed">Fixed Price</option>
                        <option value="duration_based">Duration Based</option>
                        <option value="free">Free</option>
                      </select>
                    </div>
                    {serviceForm.pricing.pricingType === "duration_based" && (
                      <div>
                        <label className="block text-sm font-medium mb-2">
                          Price per Minute ($)
                        </label>
                        <input
                          type="number"
                          value={serviceForm.pricing.pricePerMinute}
                          onChange={(e) =>
                            setServiceForm({
                              ...serviceForm,
                              pricing: {
                                ...serviceForm.pricing,
                                pricePerMinute: parseFloat(e.target.value) || 0,
                              },
                            })
                          }
                          className="w-full border rounded-lg px-3 py-2"
                          min="0"
                          step="0.01"
                        />
                      </div>
                    )}
                    <div>
                      <label className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={serviceForm.hasGST}
                          onChange={(e) =>
                            setServiceForm({
                              ...serviceForm,
                              hasGST: e.target.checked,
                            })
                          }
                          className="rounded"
                        />
                        <span>Add GST (5%)</span>
                      </label>
                    </div>
                    <div>
                      <label className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={serviceForm.hasPST}
                          onChange={(e) =>
                            setServiceForm({
                              ...serviceForm,
                              hasPST: e.target.checked,
                            })
                          }
                          className="rounded"
                        />
                        <span>Add PST (7%)</span>
                      </label>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Form Fields */}
            <div className="border-t pt-6">
              <h4 className="text-lg font-medium mb-4">Custom Form Fields</h4>

              {/* Add New Field */}
              <div className="bg-gray-50 p-4 rounded-lg mb-4">
                <h5 className="font-medium mb-3">Add New Field</h5>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <input
                    type="text"
                    placeholder="Field ID (e.g., class_type)"
                    value={newField.fieldId}
                    onChange={(e) =>
                      setNewField({ ...newField, fieldId: e.target.value })
                    }
                    className="border rounded px-3 py-2"
                  />
                  <input
                    type="text"
                    placeholder="Field Label"
                    value={newField.label}
                    onChange={(e) =>
                      setNewField({ ...newField, label: e.target.value })
                    }
                    className="border rounded px-3 py-2"
                  />
                  <select
                    value={newField.type}
                    onChange={(e) =>
                      setNewField({ ...newField, type: e.target.value })
                    }
                    className="border rounded px-3 py-2"
                  >
                    {fieldTypes.map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>

                {(newField.type === "select" ||
                  newField.type === "radio" ||
                  newField.type === "checkbox") && (
                  <div className="mt-3">
                    <label className="block text-sm font-medium mb-2">
                      Options
                    </label>
                    {newField.options.map((option, index) => (
                      <div
                        key={index}
                        className="flex items-center space-x-2 mb-2"
                      >
                        <input
                          type="text"
                          value={option}
                          onChange={(e) => {
                            const updatedOptions = [...newField.options];
                            updatedOptions[index] = e.target.value;
                            setNewField({
                              ...newField,
                              options: updatedOptions,
                            });
                          }}
                          className="flex-1 border rounded px-3 py-2"
                          placeholder="Option text"
                        />
                        {newField.options.length > 1 && (
                          <button
                            type="button"
                            onClick={() => {
                              const updatedOptions = newField.options.filter(
                                (_, i) => i !== index
                              );
                              setNewField({
                                ...newField,
                                options: updatedOptions,
                              });
                            }}
                            className="text-red-500 hover:text-red-700"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={() =>
                        setNewField({
                          ...newField,
                          options: [...newField.options, ""],
                        })
                      }
                      className="text-blue-500 hover:text-blue-700 text-sm"
                    >
                      + Add Option
                    </button>
                  </div>
                )}

                <div className="flex items-center space-x-4 mt-3">
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={newField.required}
                      onChange={(e) =>
                        setNewField({ ...newField, required: e.target.checked })
                      }
                      className="rounded"
                    />
                    <span className="text-sm">Required</span>
                  </label>
                  <button
                    type="button"
                    onClick={addFormField}
                    className="bg-blue-500 text-white px-3 py-1 rounded text-sm hover:bg-blue-600"
                  >
                    Add Field
                  </button>
                </div>
              </div>

              {/* Existing Fields */}
              {serviceForm.formFields.length > 0 && (
                <div className="space-y-2">
                  <h5 className="font-medium">Current Fields:</h5>
                  {serviceForm.formFields.map((field, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-3 border rounded"
                    >
                      <div>
                        <span className="font-medium">{field.label}</span>
                        <span className="text-gray-500 ml-2">
                          ({field.type})
                        </span>
                        {field.required && (
                          <span className="text-red-500 ml-1">*</span>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => removeFormField(index)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end space-x-3 pt-6 border-t">
              <button
                type="button"
                onClick={resetForm}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleCreateService}
                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 flex items-center space-x-2"
              >
                <Save className="w-4 h-4" />
                <span>
                  {editingService ? "Update Service" : "Create Service"}
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-semibold">Booking Management</h2>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab("services")}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === "services"
                ? "border-blue-500 text-blue-600"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
            }`}
          >
            Services
          </button>
          <button
            onClick={() => setActiveTab("bookings")}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === "bookings"
                ? "border-blue-500 text-blue-600"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
            }`}
          >
            Bookings
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === "services" && renderServicesTab()}
      {activeTab === "bookings" && renderBookingsTab()}

      {/* Service Form Modal */}
      {showServiceForm && renderServiceForm()}
    </div>
  );
};

export default BookingManagement;

// // worls well before sabadewsing
// // src/components/admin/BookingManagement.js
// import React, { useState, useEffect } from "react";
// import axios from "axios";
// import {
//   Calendar,
//   Clock,
//   DollarSign,
//   Plus,
//   Edit,
//   Trash2,
//   Save,
//   Settings,
//   Users,
//   MapPin,
//   Phone,
//   Mail,
//   FileText,
//   ToggleLeft,
//   ToggleRight,
//   X,
// } from "lucide-react";

// const BookingManagement = () => {
//   const [services, setServices] = useState([]);
//   const [activeTab, setActiveTab] = useState("services");
//   const [showServiceForm, setShowServiceForm] = useState(false);
//   const [editingService, setEditingService] = useState(null);
//   const [bookings, setBookings] = useState([]);
//   const [isMobile, setIsMobile] = useState(false);

//   const [serviceForm, setServiceForm] = useState({
//     name: "",
//     slug: "",
//     description: "",
//     category: "driving_class",
//     formFields: [],
//     availableDays: [],
//     timeSlots: [
//       {
//         startTime: "09:00",
//         endTime: "17:00",
//         duration: 60,
//         capacity: 1,
//         buffer: 15,
//       },
//     ],
//     pricing: {
//       basePrice: 0,
//       pricingType: "fixed",
//       pricePerMinute: 0,
//       optionPricing: [],
//     },
//     advanceBookingDays: 30,
//     minNoticeHours: 2,
//     maxBookingsPerCustomer: 0,
//     requiresPayment: false,
//     paymentType: "full_upfront",
//     depositAmount: 0,
//     hasGST: false,
//     hasPST: false,
//     isActive: true,
//   });

//   const [newField, setNewField] = useState({
//     fieldId: "",
//     label: "",
//     type: "text",
//     required: false,
//     placeholder: "",
//     options: [""],
//     order: 0,
//   });

//   const fieldTypes = [
//     { value: "text", label: "Text Input" },
//     { value: "textarea", label: "Text Area" },
//     { value: "select", label: "Dropdown" },
//     { value: "radio", label: "Radio Buttons" },
//     { value: "checkbox", label: "Checkboxes" },
//     { value: "date", label: "Date" },
//     { value: "time", label: "Time" },
//     { value: "number", label: "Number" },
//     { value: "email", label: "Email" },
//     { value: "phone", label: "Phone" },
//   ];

//   const categories = [
//     { value: "driving_class", label: "Driving Class" },
//     { value: "restaurant", label: "Restaurant" },
//     { value: "appointment", label: "Appointment" },
//     { value: "consultation", label: "Consultation" },
//     { value: "other", label: "Other" },
//   ];

//   const daysOfWeek = [
//     "monday",
//     "tuesday",
//     "wednesday",
//     "thursday",
//     "friday",
//     "saturday",
//     "sunday",
//   ];

//   useEffect(() => {
//     const checkIsMobile = () => setIsMobile(window.innerWidth < 768);
//     checkIsMobile();
//     window.addEventListener("resize", checkIsMobile);
//     return () => window.removeEventListener("resize", checkIsMobile);
//   }, []);

//   useEffect(() => {
//     fetchServices();
//     if (activeTab === "bookings") {
//       fetchBookings();
//     }
//   }, [activeTab]);

//   const fetchServices = async () => {
//     try {
//       const token = localStorage.getItem("token");
//       const response = await axios.get("/api/admin/booking-services", {
//         headers: { Authorization: `Bearer ${token}` },
//       });
//       setServices(response.data);
//     } catch (error) {
//       console.error("Error fetching services:", error);
//     }
//   };

//   const fetchBookings = async () => {
//     try {
//       const token = localStorage.getItem("token");
//       const response = await axios.get("/api/admin/bookings", {
//         headers: { Authorization: `Bearer ${token}` },
//       });
//       setBookings(response.data.bookings);
//     } catch (error) {
//       console.error("Error fetching bookings:", error);
//     }
//   };

//   const handleCreateService = async () => {
//     try {
//       const token = localStorage.getItem("token");

//       if (editingService) {
//         await axios.put(
//           `/api/admin/booking-services/${editingService._id}`,
//           serviceForm,
//           {
//             headers: { Authorization: `Bearer ${token}` },
//           }
//         );
//       } else {
//         await axios.post("/api/admin/booking-services", serviceForm, {
//           headers: { Authorization: `Bearer ${token}` },
//         });
//       }

//       fetchServices();
//       resetForm();
//     } catch (error) {
//       console.error("Error saving service:", error);
//       alert(
//         "Error saving service: " +
//           (error.response?.data?.error || error.message)
//       );
//     }
//   };

//   const resetForm = () => {
//     setServiceForm({
//       name: "",
//       slug: "",
//       description: "",
//       category: "driving_class",
//       formFields: [],
//       availableDays: [],
//       timeSlots: [
//         {
//           startTime: "09:00",
//           endTime: "17:00",
//           duration: 60,
//           capacity: 1,
//           buffer: 15,
//         },
//       ],
//       pricing: {
//         basePrice: 0,
//         pricingType: "fixed",
//         pricePerMinute: 0,
//         optionPricing: [],
//       },
//       advanceBookingDays: 30,
//       minNoticeHours: 2,
//       maxBookingsPerCustomer: 0,
//       requiresPayment: false,
//       paymentType: "full_upfront",
//       depositAmount: 0,
//       hasGST: false,
//       hasPST: false,
//       isActive: true,
//     });
//     setEditingService(null);
//     setShowServiceForm(false);
//   };

//   const handleEditService = (service) => {
//     setEditingService(service);
//     setServiceForm(service);
//     setShowServiceForm(true);
//   };

//   const handleDeleteService = async (serviceId) => {
//     if (!window.confirm("Are you sure you want to delete this service?"))
//       return;

//     try {
//       const token = localStorage.getItem("token");
//       await axios.delete(`/api/admin/booking-services/${serviceId}`, {
//         headers: { Authorization: `Bearer ${token}` },
//       });
//       fetchServices();
//     } catch (error) {
//       console.error("Error deleting service:", error);
//       alert("Error deleting service");
//     }
//   };

//   const addFormField = () => {
//     if (!newField.fieldId || !newField.label) {
//       alert("Please fill in field ID and label");
//       return;
//     }

//     const updatedFields = [...serviceForm.formFields, { ...newField }];
//     setServiceForm({ ...serviceForm, formFields: updatedFields });
//     setNewField({
//       fieldId: "",
//       label: "",
//       type: "text",
//       required: false,
//       placeholder: "",
//       options: [""],
//       order: 0,
//     });
//   };

//   const removeFormField = (index) => {
//     const updatedFields = serviceForm.formFields.filter((_, i) => i !== index);
//     setServiceForm({ ...serviceForm, formFields: updatedFields });
//   };

//   const addOptionToField = (fieldIndex) => {
//     const updatedFields = [...serviceForm.formFields];
//     updatedFields[fieldIndex].options.push("");
//     setServiceForm({ ...serviceForm, formFields: updatedFields });
//   };

//   const updateFieldOption = (fieldIndex, optionIndex, value) => {
//     const updatedFields = [...serviceForm.formFields];
//     updatedFields[fieldIndex].options[optionIndex] = value;
//     setServiceForm({ ...serviceForm, formFields: updatedFields });
//   };

//   const removeFieldOption = (fieldIndex, optionIndex) => {
//     const updatedFields = [...serviceForm.formFields];
//     updatedFields[fieldIndex].options.splice(optionIndex, 1);
//     setServiceForm({ ...serviceForm, formFields: updatedFields });
//   };

//   const addTimeSlot = () => {
//     const newSlot = {
//       startTime: "09:00",
//       endTime: "17:00",
//       duration: 60,
//       capacity: 1,
//       buffer: 15,
//     };
//     setServiceForm({
//       ...serviceForm,
//       timeSlots: [...serviceForm.timeSlots, newSlot],
//     });
//   };

//   const updateTimeSlot = (index, field, value) => {
//     const updatedSlots = [...serviceForm.timeSlots];
//     updatedSlots[index][field] =
//       field === "duration" || field === "capacity" || field === "buffer"
//         ? parseInt(value)
//         : value;
//     setServiceForm({ ...serviceForm, timeSlots: updatedSlots });
//   };

//   const removeTimeSlot = (index) => {
//     const updatedSlots = serviceForm.timeSlots.filter((_, i) => i !== index);
//     setServiceForm({ ...serviceForm, timeSlots: updatedSlots });
//   };

//   const toggleDay = (day) => {
//     const updatedDays = serviceForm.availableDays.includes(day)
//       ? serviceForm.availableDays.filter((d) => d !== day)
//       : [...serviceForm.availableDays, day];
//     setServiceForm({ ...serviceForm, availableDays: updatedDays });
//   };

//   const updateBookingStatus = async (bookingId, newStatus) => {
//     try {
//       const token = localStorage.getItem("token");
//       await axios.put(
//         `/api/admin/bookings/${bookingId}/status`,
//         { status: newStatus },
//         { headers: { Authorization: `Bearer ${token}` } }
//       );
//       fetchBookings();
//     } catch (error) {
//       console.error("Error updating booking status:", error);
//     }
//   };

//   const renderServicesTab = () => (
//     <div className="space-y-6">
//       <div className="flex justify-between items-center">
//         <h3 className="text-xl font-semibold">Booking Services</h3>
//         <button
//           onClick={() => setShowServiceForm(true)}
//           className="bg-blue-500 text-white px-4 py-2 rounded-lg flex items-center space-x-2 hover:bg-blue-600"
//         >
//           <Plus className="w-4 h-4" />
//           <span>New Service</span>
//         </button>
//       </div>

//       {services.length === 0 ? (
//         <div className="text-center py-8 text-gray-500">
//           No booking services created yet. Click "New Service" to get started.
//         </div>
//       ) : (
//         <div className="grid gap-4">
//           {services.map((service) => (
//             <div key={service._id} className="bg-white rounded-lg shadow p-6">
//               <div className="flex justify-between items-start">
//                 <div className="flex-1">
//                   <h4 className="text-lg font-semibold">{service.name}</h4>
//                   <p className="text-gray-600 mt-1">{service.description}</p>
//                   <div className="flex items-center space-x-4 mt-3 text-sm text-gray-500">
//                     <span className="flex items-center">
//                       <Settings className="w-4 h-4 mr-1" />
//                       {service.category.replace("_", " ")}
//                     </span>
//                     <span className="flex items-center">
//                       <DollarSign className="w-4 h-4 mr-1" />
//                       {service.requiresPayment
//                         ? `$${service.pricing.basePrice}`
//                         : "Free"}
//                     </span>
//                     <span className="flex items-center">
//                       <Clock className="w-4 h-4 mr-1" />
//                       {service.timeSlots[0]?.duration || 60} min
//                     </span>
//                   </div>
//                 </div>
//                 <div className="flex space-x-2">
//                   <button
//                     onClick={() => handleEditService(service)}
//                     className="p-2 text-blue-600 hover:bg-blue-50 rounded"
//                   >
//                     <Edit className="w-4 h-4" />
//                   </button>
//                   <button
//                     onClick={() => handleDeleteService(service._id)}
//                     className="p-2 text-red-600 hover:bg-red-50 rounded"
//                   >
//                     <Trash2 className="w-4 h-4" />
//                   </button>
//                 </div>
//               </div>
//             </div>
//           ))}
//         </div>
//       )}
//     </div>
//   );

//   const renderBookingsTab = () => (
//     <div className="space-y-6">
//       <h3 className="text-xl font-semibold">Recent Bookings</h3>

//       {bookings.length === 0 ? (
//         <div className="text-center py-8 text-gray-500">No bookings yet.</div>
//       ) : (
//         <div className="space-y-4">
//           {bookings.map((booking) => (
//             <div key={booking._id} className="bg-white rounded-lg shadow p-6">
//               <div className="flex justify-between items-start">
//                 <div>
//                   <h4 className="font-semibold">{booking.service?.name}</h4>
//                   <p className="text-gray-600">#{booking.bookingNumber}</p>
//                   <div className="flex items-center space-x-4 mt-2 text-sm text-gray-500">
//                     <span className="flex items-center">
//                       <Calendar className="w-4 h-4 mr-1" />
//                       {new Date(booking.bookingDate).toLocaleDateString()}
//                     </span>
//                     <span className="flex items-center">
//                       <Clock className="w-4 h-4 mr-1" />
//                       {booking.startTime} - {booking.endTime}
//                     </span>
//                     <span className="flex items-center">
//                       <Users className="w-4 h-4 mr-1" />
//                       {booking.customerInfo.firstName}{" "}
//                       {booking.customerInfo.lastName}
//                     </span>
//                   </div>
//                 </div>
//                 <div className="text-right">
//                   <select
//                     value={booking.status}
//                     onChange={(e) =>
//                       updateBookingStatus(booking._id, e.target.value)
//                     }
//                     className="border rounded px-3 py-1 text-sm"
//                   >
//                     <option value="pending">Pending</option>
//                     <option value="confirmed">Confirmed</option>
//                     <option value="completed">Completed</option>
//                     <option value="cancelled">Cancelled</option>
//                     <option value="no_show">No Show</option>
//                   </select>
//                   <div className="text-sm text-gray-500 mt-1">
//                     ${booking.pricing.totalAmount.toFixed(2)}
//                   </div>
//                 </div>
//               </div>
//             </div>
//           ))}
//         </div>
//       )}
//     </div>
//   );

//   const renderServiceForm = () => (
//     <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
//       <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
//         <div className="p-6">
//           <div className="flex justify-between items-center mb-6">
//             <h3 className="text-xl font-semibold">
//               {editingService ? "Edit Service" : "Create New Service"}
//             </h3>
//             <button
//               onClick={resetForm}
//               className="text-gray-500 hover:text-gray-700"
//             >
//               <X className="w-6 h-6" />
//             </button>
//           </div>

//           <div className="space-y-6">
//             {/* Basic Info */}
//             <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
//               <div>
//                 <label className="block text-sm font-medium mb-2">
//                   Service Name
//                 </label>
//                 <input
//                   type="text"
//                   value={serviceForm.name}
//                   onChange={(e) =>
//                     setServiceForm({ ...serviceForm, name: e.target.value })
//                   }
//                   className="w-full border rounded-lg px-3 py-2"
//                   placeholder="e.g., Driving Lesson Class 5"
//                 />
//               </div>
//               <div>
//                 <label className="block text-sm font-medium mb-2">
//                   URL Slug
//                 </label>
//                 <input
//                   type="text"
//                   value={serviceForm.slug}
//                   onChange={(e) =>
//                     setServiceForm({ ...serviceForm, slug: e.target.value })
//                   }
//                   className="w-full border rounded-lg px-3 py-2"
//                   placeholder="driving-lesson-class-5"
//                 />
//               </div>
//               <div className="md:col-span-2">
//                 <label className="block text-sm font-medium mb-2">
//                   Description
//                 </label>
//                 <textarea
//                   value={serviceForm.description}
//                   onChange={(e) =>
//                     setServiceForm({
//                       ...serviceForm,
//                       description: e.target.value,
//                     })
//                   }
//                   className="w-full border rounded-lg px-3 py-2"
//                   rows="3"
//                   placeholder="Describe your service..."
//                 />
//               </div>
//               <div>
//                 <label className="block text-sm font-medium mb-2">
//                   Category
//                 </label>
//                 <select
//                   value={serviceForm.category}
//                   onChange={(e) =>
//                     setServiceForm({ ...serviceForm, category: e.target.value })
//                   }
//                   className="w-full border rounded-lg px-3 py-2"
//                 >
//                   {categories.map((cat) => (
//                     <option key={cat.value} value={cat.value}>
//                       {cat.label}
//                     </option>
//                   ))}
//                 </select>
//               </div>
//             </div>

//             {/* Available Days */}
//             <div>
//               <label className="block text-sm font-medium mb-2">
//                 Available Days
//               </label>
//               <div className="flex flex-wrap gap-2">
//                 {daysOfWeek.map((day) => (
//                   <button
//                     key={day}
//                     type="button"
//                     onClick={() => toggleDay(day)}
//                     className={`px-3 py-1 rounded-full text-sm ${
//                       serviceForm.availableDays.includes(day)
//                         ? "bg-blue-500 text-white"
//                         : "bg-gray-200 text-gray-700"
//                     }`}
//                   >
//                     {day.charAt(0).toUpperCase() + day.slice(1)}
//                   </button>
//                 ))}
//               </div>
//             </div>

//             {/* Time Slots */}
//             <div>
//               <div className="flex justify-between items-center mb-2">
//                 <label className="block text-sm font-medium">Time Slots</label>
//                 <button
//                   type="button"
//                   onClick={addTimeSlot}
//                   className="text-blue-500 hover:text-blue-700 text-sm"
//                 >
//                   + Add Slot
//                 </button>
//               </div>
//               <div className="space-y-2">
//                 {serviceForm.timeSlots.map((slot, index) => (
//                   <div key={index} className="p-3 border rounded">
//                     {/* Responsive grid layout */}
//                     <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
//                       {/* Start time input */}
//                       <div>
//                         <input
//                           type="time"
//                           value={slot.startTime}
//                           onChange={(e) =>
//                             updateTimeSlot(index, "startTime", e.target.value)
//                           }
//                           className="border rounded px-2 py-1 w-full"
//                         />
//                       </div>

//                       {/* End time input */}
//                       <div>
//                         <input
//                           type="time"
//                           value={slot.endTime}
//                           onChange={(e) =>
//                             updateTimeSlot(index, "endTime", e.target.value)
//                           }
//                           className="border rounded px-2 py-1 w-full"
//                         />
//                       </div>

//                       {/* Duration input */}
//                       <div>
//                         <input
//                           type="number"
//                           value={slot.duration}
//                           onChange={(e) =>
//                             updateTimeSlot(index, "duration", e.target.value)
//                           }
//                           placeholder="Duration (min)"
//                           className="border rounded px-2 py-1 w-full"
//                         />
//                         <p className="text-xs text-gray-500 mt-1">
//                           The duration
//                         </p>
//                       </div>

//                       {/* Buffer input */}
//                       <div>
//                         <input
//                           type="number"
//                           value={slot.buffer}
//                           onChange={(e) =>
//                             updateTimeSlot(index, "buffer", e.target.value)
//                           }
//                           placeholder="Buffer (min)"
//                           className="border rounded px-2 py-1 w-full"
//                         />
//                         <p className="text-xs text-gray-500 mt-1">
//                           The buffer between time slots
//                         </p>
//                       </div>

//                       {/* Delete button */}
//                       <div className="flex items-start justify-center lg:justify-start">
//                         {serviceForm.timeSlots.length > 1 && (
//                           <button
//                             type="button"
//                             onClick={() => removeTimeSlot(index)}
//                             className="text-red-500 hover:text-red-700 mt-2"
//                           >
//                             <Trash2 className="w-4 h-4" />
//                           </button>
//                         )}
//                       </div>
//                     </div>
//                   </div>
//                 ))}
//               </div>
//             </div>

//             {/* Payment Settings */}
//             <div className="border-t pt-6">
//               <h4 className="text-lg font-medium mb-4">Payment Settings</h4>
//               <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
//                 <div>
//                   <label className="flex items-center space-x-2">
//                     <input
//                       type="checkbox"
//                       checked={serviceForm.requiresPayment}
//                       onChange={(e) =>
//                         setServiceForm({
//                           ...serviceForm,
//                           requiresPayment: e.target.checked,
//                         })
//                       }
//                       className="rounded"
//                     />
//                     <span>Requires Payment</span>
//                   </label>
//                 </div>
//                 {serviceForm.requiresPayment && (
//                   <>
//                     <div>
//                       <label className="block text-sm font-medium mb-2">
//                         Base Price ($)
//                       </label>
//                       <input
//                         type="number"
//                         value={serviceForm.pricing.basePrice}
//                         onChange={(e) =>
//                           setServiceForm({
//                             ...serviceForm,
//                             pricing: {
//                               ...serviceForm.pricing,
//                               basePrice: parseFloat(e.target.value) || 0,
//                             },
//                           })
//                         }
//                         className="w-full border rounded-lg px-3 py-2"
//                         min="0"
//                         step="0.01"
//                       />
//                     </div>
//                     <div>
//                       <label className="block text-sm font-medium mb-2">
//                         Pricing Type
//                       </label>
//                       <select
//                         value={serviceForm.pricing.pricingType}
//                         onChange={(e) =>
//                           setServiceForm({
//                             ...serviceForm,
//                             pricing: {
//                               ...serviceForm.pricing,
//                               pricingType: e.target.value,
//                             },
//                           })
//                         }
//                         className="w-full border rounded-lg px-3 py-2"
//                       >
//                         <option value="fixed">Fixed Price</option>
//                         <option value="duration_based">Duration Based</option>
//                         <option value="free">Free</option>
//                       </select>
//                     </div>
//                     {serviceForm.pricing.pricingType === "duration_based" && (
//                       <div>
//                         <label className="block text-sm font-medium mb-2">
//                           Price per Minute ($)
//                         </label>
//                         <input
//                           type="number"
//                           value={serviceForm.pricing.pricePerMinute}
//                           onChange={(e) =>
//                             setServiceForm({
//                               ...serviceForm,
//                               pricing: {
//                                 ...serviceForm.pricing,
//                                 pricePerMinute: parseFloat(e.target.value) || 0,
//                               },
//                             })
//                           }
//                           className="w-full border rounded-lg px-3 py-2"
//                           min="0"
//                           step="0.01"
//                         />
//                       </div>
//                     )}
//                     <div>
//                       <label className="flex items-center space-x-2">
//                         <input
//                           type="checkbox"
//                           checked={serviceForm.hasGST}
//                           onChange={(e) =>
//                             setServiceForm({
//                               ...serviceForm,
//                               hasGST: e.target.checked,
//                             })
//                           }
//                           className="rounded"
//                         />
//                         <span>Add GST (5%)</span>
//                       </label>
//                     </div>
//                     <div>
//                       <label className="flex items-center space-x-2">
//                         <input
//                           type="checkbox"
//                           checked={serviceForm.hasPST}
//                           onChange={(e) =>
//                             setServiceForm({
//                               ...serviceForm,
//                               hasPST: e.target.checked,
//                             })
//                           }
//                           className="rounded"
//                         />
//                         <span>Add PST (7%)</span>
//                       </label>
//                     </div>
//                   </>
//                 )}
//               </div>
//             </div>

//             {/* Form Fields */}
//             <div className="border-t pt-6">
//               <h4 className="text-lg font-medium mb-4">Custom Form Fields</h4>

//               {/* Add New Field */}
//               <div className="bg-gray-50 p-4 rounded-lg mb-4">
//                 <h5 className="font-medium mb-3">Add New Field</h5>
//                 <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
//                   <input
//                     type="text"
//                     placeholder="Field ID (e.g., class_type)"
//                     value={newField.fieldId}
//                     onChange={(e) =>
//                       setNewField({ ...newField, fieldId: e.target.value })
//                     }
//                     className="border rounded px-3 py-2"
//                   />
//                   <input
//                     type="text"
//                     placeholder="Field Label"
//                     value={newField.label}
//                     onChange={(e) =>
//                       setNewField({ ...newField, label: e.target.value })
//                     }
//                     className="border rounded px-3 py-2"
//                   />
//                   <select
//                     value={newField.type}
//                     onChange={(e) =>
//                       setNewField({ ...newField, type: e.target.value })
//                     }
//                     className="border rounded px-3 py-2"
//                   >
//                     {fieldTypes.map((type) => (
//                       <option key={type.value} value={type.value}>
//                         {type.label}
//                       </option>
//                     ))}
//                   </select>
//                 </div>

//                 {(newField.type === "select" ||
//                   newField.type === "radio" ||
//                   newField.type === "checkbox") && (
//                   <div className="mt-3">
//                     <label className="block text-sm font-medium mb-2">
//                       Options
//                     </label>
//                     {newField.options.map((option, index) => (
//                       <div
//                         key={index}
//                         className="flex items-center space-x-2 mb-2"
//                       >
//                         <input
//                           type="text"
//                           value={option}
//                           onChange={(e) => {
//                             const updatedOptions = [...newField.options];
//                             updatedOptions[index] = e.target.value;
//                             setNewField({
//                               ...newField,
//                               options: updatedOptions,
//                             });
//                           }}
//                           className="flex-1 border rounded px-3 py-2"
//                           placeholder="Option text"
//                         />
//                         {newField.options.length > 1 && (
//                           <button
//                             type="button"
//                             onClick={() => {
//                               const updatedOptions = newField.options.filter(
//                                 (_, i) => i !== index
//                               );
//                               setNewField({
//                                 ...newField,
//                                 options: updatedOptions,
//                               });
//                             }}
//                             className="text-red-500 hover:text-red-700"
//                           >
//                             <X className="w-4 h-4" />
//                           </button>
//                         )}
//                       </div>
//                     ))}
//                     <button
//                       type="button"
//                       onClick={() =>
//                         setNewField({
//                           ...newField,
//                           options: [...newField.options, ""],
//                         })
//                       }
//                       className="text-blue-500 hover:text-blue-700 text-sm"
//                     >
//                       + Add Option
//                     </button>
//                   </div>
//                 )}

//                 <div className="flex items-center space-x-4 mt-3">
//                   <label className="flex items-center space-x-2">
//                     <input
//                       type="checkbox"
//                       checked={newField.required}
//                       onChange={(e) =>
//                         setNewField({ ...newField, required: e.target.checked })
//                       }
//                       className="rounded"
//                     />
//                     <span className="text-sm">Required</span>
//                   </label>
//                   <button
//                     type="button"
//                     onClick={addFormField}
//                     className="bg-blue-500 text-white px-3 py-1 rounded text-sm hover:bg-blue-600"
//                   >
//                     Add Field
//                   </button>
//                 </div>
//               </div>

//               {/* Existing Fields */}
//               {serviceForm.formFields.length > 0 && (
//                 <div className="space-y-2">
//                   <h5 className="font-medium">Current Fields:</h5>
//                   {serviceForm.formFields.map((field, index) => (
//                     <div
//                       key={index}
//                       className="flex items-center justify-between p-3 border rounded"
//                     >
//                       <div>
//                         <span className="font-medium">{field.label}</span>
//                         <span className="text-gray-500 ml-2">
//                           ({field.type})
//                         </span>
//                         {field.required && (
//                           <span className="text-red-500 ml-1">*</span>
//                         )}
//                       </div>
//                       <button
//                         type="button"
//                         onClick={() => removeFormField(index)}
//                         className="text-red-500 hover:text-red-700"
//                       >
//                         <Trash2 className="w-4 h-4" />
//                       </button>
//                     </div>
//                   ))}
//                 </div>
//               )}
//             </div>

//             {/* Action Buttons */}
//             <div className="flex justify-end space-x-3 pt-6 border-t">
//               <button
//                 type="button"
//                 onClick={resetForm}
//                 className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
//               >
//                 Cancel
//               </button>
//               <button
//                 type="button"
//                 onClick={handleCreateService}
//                 className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 flex items-center space-x-2"
//               >
//                 <Save className="w-4 h-4" />
//                 <span>
//                   {editingService ? "Update Service" : "Create Service"}
//                 </span>
//               </button>
//             </div>
//           </div>
//         </div>
//       </div>
//     </div>
//   );

//   return (
//     <div className="space-y-6">
//       <h2 className="text-2xl font-semibold">Booking Management</h2>

//       {/* Tab Navigation */}
//       <div className="border-b border-gray-200">
//         <nav className="-mb-px flex space-x-8">
//           <button
//             onClick={() => setActiveTab("services")}
//             className={`py-2 px-1 border-b-2 font-medium text-sm ${
//               activeTab === "services"
//                 ? "border-blue-500 text-blue-600"
//                 : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
//             }`}
//           >
//             Services
//           </button>
//           <button
//             onClick={() => setActiveTab("bookings")}
//             className={`py-2 px-1 border-b-2 font-medium text-sm ${
//               activeTab === "bookings"
//                 ? "border-blue-500 text-blue-600"
//                 : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
//             }`}
//           >
//             Bookings
//           </button>
//         </nav>
//       </div>

//       {/* Tab Content */}
//       {activeTab === "services" && renderServicesTab()}
//       {activeTab === "bookings" && renderBookingsTab()}

//       {/* Service Form Modal */}
//       {showServiceForm && renderServiceForm()}
//     </div>
//   );
// };

// export default BookingManagement;
